/* eslint-disable */
import React, { ChangeEvent, FocusEvent, FC, useState, useEffect } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import decoratePlaceholderWithModifers from '@src/containers/stepContainer/dataTransform/decoratePlaceholderWithModifiers';

const styleSheet = (theme: any) => ({
  input: {
    position: 'relative',
    '&::placeholder': {
      textOverflow: 'ellipsis !important',
      color: '#b7c1e1',
    },
    '&:-ms-input-placeholder': {
      textOverflow: 'ellipsis !important',
      color: '#b7c1e1',
    },
    '&:focus': {
      border: 'solid 2px #085fe5',
    },
    '&:disabled': {
      color: '#9fabc8',
      background: '#eaeaf1',
    },
    outline: 0,
    width: '100%',
    height: '72px',
    objectFit: 'contain',
    borderRadius: '6px',
    border: 'solid 2px #b7c1e1',
    boxSizing: 'border-box',
    padding: '24px 27px',
    fontSize: '24px',
    color: '#4f5b84',
    fontFamily: 'CircularStd, sans-serif',
  },
  error: {
    borderColor: '#e60000 !important',
    transition: 'border 500ms',
  },
  label: {
    ...theme.label,
    display: 'block',
    textAlign: 'left',
    textTransform: 'uppercase',
    paddingLeft: '9px',
    marginBottom: '14px',
    color: '#223762',
    fontWeight: '800',
  },
  hideOnMobile: {},
  wrapper: {
    flex: 1,
  },
  '@media (max-width: 900px)': {
    input: {
      height: '64px',
      fontSize: '20px',
    },
    label: {
      fontSize: '10px',
    },
    hideOnMobile: {
      display: 'none',
    },
  },
  modifiedPlaceHolder: {
    position: 'absolute',
    left: '44px',
    top: '55px',
    maxWidth: '90%',
    color: '#b7c1e1',
    fontSize: '24px',
    textAlign: 'left',
    textWrap: 'nowrap',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media (max-width: 900px)': {
      left: '30px',
      top: '47px',
      maxWidth: '80%',
      fontSize: '20px',
    },
  },
  fade: {
    animationName: 'fade',
    animationDuration: '3.2s',
  },
  '@keyframes fade': {
    '0%, 100%': { opacity: 0.3 },
    '15%, 85%': { opacity: 1 },
  },
});

interface Props {
  // classes, placeholder, onChange, value, name, onBlur
  error?: boolean;
  max?: string;
  min?: string;
  label?: string;
  type: string;
  name: string;
  value?: string;
  classes?: any;
  placeholder?: string;
  onChange?: (e: object) => void;
  onBlur?: (e: FocusEvent<any>) => void;
  step?: string;
  autoCompleteType?: string;
  disabled?: boolean;
  required?: boolean;
}

const Input: FC<Props> = (props: Props) => {
  const inputRef = React.useRef(null);

  const handleChange = (event: ChangeEvent<any>) => {
    if (props.onChange) {
      //Look at the id, instead of the name
      const { id, value } = event.target;
      const fieldId = id; //With autocomplete now using the name attribute on an input, we will look for the id instead.
      const response = {
        value: value,
        name: fieldId,
        fieldId,
      };
      //add the original event in order to clear the sat, gpa and act fields if the value
      //entered is not numeric
      props.onChange({ target: response, originalEvent: event });
    }
  };

  // sets focus to input when clicking on placeholder span
  const handlePlaceholderSpanClick = () => {
    inputRef.current.focus();
  };

  // checks for surrounding brackets {} that indicate placeholder text is modified
  const isPlaceholderModified = /^{.*}$/.test(props.placeholder);

  const [currentPlaceholder, setCurrentPlaceholder] = useState(
    isPlaceholderModified
      ? decoratePlaceholderWithModifers(props.placeholder).text[0]
      : ''
  );

  let placeholderParsed: any = {};
  if (isPlaceholderModified) {
    placeholderParsed = decoratePlaceholderWithModifers(props.placeholder);

    useEffect(() => {
      let i = 0;
      const intervalId = setInterval(() => {
        // checks if i is less than text array length, otherwise resets to 0 to repeat cycle
        i = i < placeholderParsed.text.length - 1 ? i + 1 : 0;
        setCurrentPlaceholder(placeholderParsed.text[i]);
      }, 3000); // Change placeholder every 3 seconds

      return () => clearInterval(intervalId);
    }, [props.placeholder]);
  }

  return (
    <div className={props.classes.wrapper}>
      <label
        className={classNames(props.classes.label, {
          [props.classes.hideOnMobile]: props.label.length == 1,
        })}
      >
        {props.label}
      </label>
      <input
        autoComplete={props.autoCompleteType}
        style={{
          textTransform: props.type == 'date' ? 'uppercase' : 'inherit',
        }}
        className={classNames(props.classes.input, {
          [props.classes.error]: props.error,
        })}
        placeholder={isPlaceholderModified ? '' : props.placeholder}
        type={props.type}
        id={props.name}
        name={props.autoCompleteType}
        aria-label={props.required ? `${props.label}. Required field` : props.label}
        value={props.value}
        onChange={e => handleChange(e)}
        onBlur={props.onBlur}
        max={props.max}
        min={props.min}
        step={props.step}
        ref={inputRef}
        disabled={props.disabled}
      />
      {isPlaceholderModified && !props.value && (
        <span
          key={currentPlaceholder}
          onClick={handlePlaceholderSpanClick}
          className={classNames(
            props.classes.modifiedPlaceHolder,
            props.classes[placeholderParsed.animation[0]]
          )}
        >
          {currentPlaceholder}
        </span>
      )}
    </div>
  );
};

export default injectSheet(styleSheet)(Input);
